import { cva, type VariantProps } from "class-variance-authority";
import { useId, type ComponentPropsWithoutRef, type ReactNode } from "react";

import { cn } from "@/lib/cn";

const field = cva(
  "flex w-full items-center gap-2.5 transition-colors focus-within:outline-2 focus-within:outline-offset-2 focus-within:outline-ink",
  {
    variants: {
      variant: {
        /** 흰 면에 테두리 — 로그인 */
        outline: "bg-surface border-line-strong border",
        /** 옅은 면, 테두리 없음 — 검색, 모달 안 입력 */
        filled: "bg-paper",
        /** 모달 안의 조금 더 따뜻한 면 */
        sunken: "bg-surface-3",
      },
      size: {
        sm: "h-10 rounded-[10px] px-3.5 text-sm",
        md: "h-12 rounded-xl px-4 text-[15px]",
        lg: "h-13 rounded-[14px] px-4.5 text-[15px]",
        xl: "h-[58px] rounded-xl px-5 text-[17px]",
      },
    },
    defaultVariants: { variant: "outline", size: "md" },
  },
);

type TextFieldProps = VariantProps<typeof field> & {
  /** 화면에 보이지 않더라도 접근성상 반드시 있어야 한다. */
  label: string;
  /** 라벨을 눈에 보이게 띄울지. 시안은 전부 숨김이다. */
  labelHidden?: boolean;
  /** 앞에 붙는 아이콘 — 검색 돋보기 */
  leading?: ReactNode;
  /** 뒤에 붙는 것 — 지우기 버튼 등 */
  trailing?: ReactNode;
} & Omit<ComponentPropsWithoutRef<"input">, "size" | "className">;

export function TextField({
  label,
  labelHidden = true,
  leading,
  trailing,
  variant,
  size,
  id,
  ...rest
}: TextFieldProps) {
  const generatedId = useId();
  const inputId = id ?? generatedId;

  return (
    <div className="flex w-full flex-col gap-1.5">
      <label
        htmlFor={inputId}
        className={cn(
          "text-muted text-[13px] font-semibold",
          labelHidden && "sr-only",
        )}
      >
        {label}
      </label>

      <div className={field({ variant, size })}>
        {leading ? (
          <span className="text-muted-3 flex shrink-0 items-center [&_svg]:size-4.5">
            {leading}
          </span>
        ) : null}

        <input
          id={inputId}
          className="text-ink placeholder:text-muted-3 min-w-0 flex-1 bg-transparent outline-none"
          {...rest}
        />

        {trailing ? <span className="flex shrink-0">{trailing}</span> : null}
      </div>
    </div>
  );
}
